import CategoryIcon from "owa-categories/lib/components/CategoryIcon";
import { observer } from "owa-mobx-react";
import { getDensityModeCssClass, getDensityModeString } from "owa-fabric-theme";
import React from "react";
import { isReadingPanePositionOff, isSingleLineListView } from "owa-mail-layout";
/* eslint-disable-next-line @fluentui/react-components/prefer-fluentui-v9 -- (https://aka.ms/OWALintWiki)
 * Baseline. DO NOT COPY AND PASTE! Substitute this line with an explanation or resolve the problem by using v9 components instead
 *	> Avoid using Icon from '@fluentui/react', as this package has already migrated to Fluent UI 9. Use a JSX SVG icon from '@fluentui/react-icons' instead. */
import { Icon } from "@fluentui/react/lib/Icon";
import { default as ChevronDown } from "owa-fluent-icons-svg/lib/icons/ChevronDownRegular";
import { default as ChevronRight } from "owa-fluent-icons-svg/lib/icons/ChevronRightRegular";
import isSenderImageEnabled from "owa-messagelist-layout-option/lib/utils/isSenderImageEnabled";
import shouldAlwaysShowMessageListCheckboxes from "owa-messagelist-layout-option/lib/utils/shouldAlwaysShowMessageListCheckboxes";
import shouldHideMessageListCheckboxes from "owa-messagelist-layout-option/lib/utils/shouldHideMessageListCheckboxes";
import type { TableView, MailFolderTableQuery } from "owa-mail-list-store";
import { type SortColumn, getIsSearchTableShown } from "owa-mail-list-store";
import { useComputedValue } from "owa-react-hooks/lib/useComputed";
import { getMailboxInfo } from "owa-mail-mailboxinfo";
import { showMailItemContextMenu } from "owa-mail-list-actions/lib/actions/itemContextMenuActions";
import MailListContextMenuSource from "owa-mail-list-store/lib/store/schema/MailListContextMenuSource";
import MailListContextMenuType from "owa-mail-list-store/lib/store/schema/MailListContextMenuType";
import { getAnchorForContextMenu } from "owa-positioning";
import { Button } from "@fluentui/react-components";
import { useKeydownHandler } from "owa-hotkeys";
import { MoreHorizontalRegular, MoreHorizontalFilled, bundleIcon } from "@fluentui/react-icons";
import loc from "owa-localize";
import { headerActionMenu } from "./MailListGroupHeader.locstring.json";

import { full, fullTypeRamp, medium, mediumTypeRamp, compact, compactTypeRamp, outerContainer, outerContainerWithActions, outerContainerBleedthrough, groupHeaderClickable, scl, mcl, senderImageOn as styles_senderImageOn, selectionModeActivated, noCheckboxStyles, iconContainer, categoryIcon, groupHeaderAction, moreButton, moreIcon, sclMoreButton, mclMoreButton, innerTextContainer, groupHeaderFocusOutline, fullBigPersonaStyles, mediumBigPersonaStyles, compactBigPersonaStyles, } from "./MailListGroupHeader.scss";

import classnames from "owa-classnames";
import { toggleVirtualizedGroupHeaderCollapse, virtualizedMailGroupHeadersStore, } from "owa-virtualized-group-headers";
import { singleSelectRow } from "owa-mail-actions/lib/singleSelectRow";
import MailListItemSelectionSource from "owa-mail-store/lib/store/schema/MailListItemSelectionSource";
import { useIsTypeRampUpdatesEnabled } from "owa-mail-list-item-shared/lib/utils/useIsTypeRampUpdatesEnabled";
import { isUnifiedInboxMailboxInfo } from "owa-client-types/lib/UnifiedInboxMailboxInfo";
import { isBigPersonaLayoutEnabled } from "owa-mail-list-item-shared";

// Constants for padding calculations
const PADDING_VALUES = {
    SCL: {
        full: {
            withChevron: { withSenderOrSelection: 40, withoutSender: 10 },
            withoutChevron: { withSenderOrSelection: 72, withoutSender: 40 },
        },
        medium: {
            withChevron: { withSenderOrSelection: 40, withoutSender: 14 },
            withoutChevron: { withSenderOrSelection: 68, withoutSender: 40 },
        },
        compact: {
            withChevron: { withSenderOrSelection: 38, withoutSender: 14 },
            withoutChevron: { withSenderOrSelection: 64, withoutSender: 40 },
        },
    },
    MCL: {
        withChevron: 38,
        withoutChevron: { full: 68, other: 64 },
    },
};

const MoreHorizontal = bundleIcon(MoreHorizontalFilled, MoreHorizontalRegular);

/**
 * Calculates the left padding for the group header based on various UI state conditions
 */
const calculateGroupHeaderPaddingLeft = (isSCL: boolean, densityModeString: "full" | "medium" | "compact", senderImageEnabled: boolean, isChevronVisible: boolean, isSelectionModeActivated: boolean, areCheckboxesHidden: boolean, shouldUseUpdatedPadding: boolean): number => {
    // Hiding the checkbox collapses the inset.
    if (areCheckboxesHidden) {
        if (!isChevronVisible) {
            return 38;
        }
        return densityModeString === "full" ? 10 : 14;
    }
    if (isSCL) {
        const paddingConfig = PADDING_VALUES.SCL[densityModeString];
        
        if (!paddingConfig) {
            return 0;
        }
        
        const chevronConfig = isChevronVisible
            ? paddingConfig.withChevron
            : paddingConfig.withoutChevron;
        
        if (!isChevronVisible &&
            (senderImageEnabled || isSelectionModeActivated) &&
            shouldUseUpdatedPadding) {
            return densityModeString === "compact" ? 66 : 72;
        }
        
        return senderImageEnabled || isSelectionModeActivated
            ? chevronConfig.withSenderOrSelection
            : chevronConfig.withoutSender;
    }
    else {
        // MCL (Multi-Column Layout)
        if (isChevronVisible) {
            return PADDING_VALUES.MCL.withChevron;
        }
        else {
            return densityModeString === "full"
                ? PADDING_VALUES.MCL.withoutChevron.full
                : PADDING_VALUES.MCL.withoutChevron.other;
        }
    }
};

export interface MailListGroupHeaderProps {
    headerText: string;
    tableView: TableView;
    onCollapse: () => void;
}

const MailListGroupHeader: React.FunctionComponent<MailListGroupHeaderProps> = observer(function MailListGroupHeader(props: MailListGroupHeaderProps) {
    const { headerText, tableView, onCollapse } = props;
    const elementRef = React.useRef<HTMLDivElement | null>(null);
    const isSCL = !isSingleLineListView();
    const isTypeRampUpdatesEnabled = useIsTypeRampUpdatesEnabled();
    const isBigPersonaEnabled = isBigPersonaLayoutEnabled();
    const densityModeCssClass = isTypeRampUpdatesEnabled
        ? getDensityModeCssClass(fullTypeRamp, mediumTypeRamp, compactTypeRamp)
        : getDensityModeCssClass(full, medium, compact);
    const bigPersonaDensityStyles = getDensityModeCssClass(fullBigPersonaStyles, mediumBigPersonaStyles, compactBigPersonaStyles);
    const densityModeString = getDensityModeString();
    const senderImageEnabled = isSenderImageEnabled();
    const hasSenderImageOrPersistentCheckbox = senderImageEnabled || shouldAlwaysShowMessageListCheckboxes();
    const areCheckboxesHidden = shouldHideMessageListCheckboxes();
    const isTableInSelectionMode = tableView?.isInCheckedMode || tableView?.isInSelectAllState;
    const isSelectionModeActivated = getIsSearchTableShown() || isTableInSelectionMode;
    const mailboxInfo = getMailboxInfo(tableView);
    
    const rowId = "groupHeader" + headerText;
    
    const groupHeaderData = virtualizedMailGroupHeadersStore().virtualizedGroupHeaders.get(headerText);
    
    const isLoadingOfRowKeysComplete = groupHeaderData?.isLoadingOfRowKeysComplete ?? false;
    
    const areHeaderActionsEnabled = !isUnifiedInboxMailboxInfo(mailboxInfo) && isLoadingOfRowKeysComplete;
    
    const firstRowKey = groupHeaderData?.rowKeys[0];
    
    const headerClassNames = React.useMemo(() => classnames(outerContainer, areHeaderActionsEnabled && outerContainerWithActions, isTypeRampUpdatesEnabled && outerContainerBleedthrough, isLoadingOfRowKeysComplete && groupHeaderClickable, areHeaderActionsEnabled && groupHeaderAction, isReadingPanePositionOff()
        ? "singleLineCirclePersonaDivWidth"
        : "threeColumnCirclePersonaDivWidth", densityModeCssClass, isBigPersonaEnabled && bigPersonaDensityStyles, groupHeaderFocusOutline), [
        isLoadingOfRowKeysComplete,
        areHeaderActionsEnabled,
        densityModeCssClass,
        bigPersonaDensityStyles,
    ]);
    
    const onVirtualizedHeaderClick = React.useCallback((evt: React.MouseEvent<HTMLElement> | KeyboardEvent) => {
        toggleVirtualizedGroupHeaderCollapse(headerText, tableView);
        
        if (groupHeaderData?.isCollapsed && firstRowKey) {
            // If the group header is collapsed, select the first row in the group
            // and focus on the header to keep focus on what's visible.
            singleSelectRow(tableView, firstRowKey, false /* isUserNavigation */, MailListItemSelectionSource.MailListGroupHeader, evt.timeStamp, headerText);
        }
        
        onCollapse();
    }, [onCollapse, tableView, headerText, firstRowKey, groupHeaderData?.isCollapsed]);
    
    const getOnHeaderContextMenu = React.useCallback((selectionSource: MailListItemSelectionSource) => (evt: React.MouseEvent<HTMLElement> | KeyboardEvent) => {
        evt.stopPropagation();
        evt.preventDefault();
        
        if (firstRowKey) {
            singleSelectRow(tableView, firstRowKey, false /* isUserNavigation */, selectionSource, evt.timeStamp, headerText);
        }
        
        showMailItemContextMenu(getAnchorForContextMenu(evt), MailListContextMenuType.All, MailListContextMenuSource.MailListGroupHeader, elementRef.current);
    }, [tableView, headerText, firstRowKey]);
    
    const iconClassName = classnames(iconContainer, isSCL ? scl : mcl, hasSenderImageOrPersistentCheckbox && styles_senderImageOn, isSelectionModeActivated && selectionModeActivated, areCheckboxesHidden && noCheckboxStyles, densityModeCssClass, isBigPersonaEnabled && bigPersonaDensityStyles);
    
    const groupHeaderChevronIcon = groupHeaderData?.isCollapsed ? ChevronRight : ChevronDown;
    
    const moreButtonClass = classnames(moreButton, isSCL ? sclMoreButton : mclMoreButton);
    
    const moreHorizontalIcon = React.useMemo(() => <MoreHorizontal className={classnames(densityModeCssClass, moreIcon)}/>, [densityModeCssClass]);
    
    const outerContainerPaddingLeft = useComputedValue(() => {
        const paddingLeft = calculateGroupHeaderPaddingLeft(isSCL, densityModeString, hasSenderImageOrPersistentCheckbox, isLoadingOfRowKeysComplete, isSelectionModeActivated, areCheckboxesHidden, isBigPersonaLayoutEnabled());
        
        return {
            paddingLeft: `${paddingLeft}px`,
        };
    }, [
        isSCL,
        densityModeString,
        hasSenderImageOrPersistentCheckbox,
        isLoadingOfRowKeysComplete,
        isSelectionModeActivated,
        areCheckboxesHidden,
        isTypeRampUpdatesEnabled,
        isBigPersonaLayoutEnabled(),
    ]);
    
    const onRightArrowExpand = React.useCallback((evt: KeyboardEvent) => {
        if (groupHeaderData?.isCollapsed && isLoadingOfRowKeysComplete) {
            onVirtualizedHeaderClick(evt);
        }
    }, [groupHeaderData?.isCollapsed, isLoadingOfRowKeysComplete, onVirtualizedHeaderClick]);
    
    const onLeftArrowCollapse = React.useCallback((evt: KeyboardEvent) => {
        if (!groupHeaderData?.isCollapsed && isLoadingOfRowKeysComplete) {
            onVirtualizedHeaderClick(evt);
        }
    }, [groupHeaderData?.isCollapsed, isLoadingOfRowKeysComplete, onVirtualizedHeaderClick]);
    
    useKeydownHandler(elementRef, "enter", onVirtualizedHeaderClick);
    useKeydownHandler(elementRef, "right", onRightArrowExpand);
    useKeydownHandler(elementRef, "left", onLeftArrowCollapse);
    
    React.useEffect(() => {
        if (tableView?.headerToFocus === headerText && elementRef.current) {
            elementRef.current.focus();
        }
    }, [tableView?.headerToFocus, headerText]);
    
    return (<div id={rowId} role="button" tabIndex={-1} ref={elementRef} aria-label={headerText} className={headerClassNames} style={outerContainerPaddingLeft} onClick={isLoadingOfRowKeysComplete ? onVirtualizedHeaderClick : undefined} onContextMenu={areHeaderActionsEnabled && !getIsSearchTableShown()
            ? getOnHeaderContextMenu(MailListItemSelectionSource.GroupHeaderContextMenu)
            : undefined} aria-expanded={groupHeaderData?.isCollapsed === undefined
            ? undefined
            : !groupHeaderData?.isCollapsed}>
                {isLoadingOfRowKeysComplete && (<Icon className={iconClassName} iconName={groupHeaderChevronIcon}/>)}
                {(tableView?.tableQuery as MailFolderTableQuery)?.sortBy?.sortColumn === 14 ? (<CategoryIcon categoryName={headerText} mailboxInfo={mailboxInfo} iconClassName={categoryIcon}/>) : null}
                <div title={headerText} className={innerTextContainer}>
                    {headerText}
                </div>
                {areHeaderActionsEnabled && !getIsSearchTableShown() && (<Button appearance="transparent" className={moreButtonClass} icon={moreHorizontalIcon} aria-label={loc(headerActionMenu)} tabIndex={-1} onClick={getOnHeaderContextMenu(MailListItemSelectionSource.MailListGroupHeaderEllipsis)}/>)}
            </div>);
}, "MailListGroupHeader");
export default MailListGroupHeader;
